<template>
  <div class="teams-page bg-slate-50 min-h-screen">
    
    <div class="container mx-auto py-8 px-4 md:px-6">
      <!-- Team Members Card -->
      <div class="card mb-8">
        <div class="card-header flex justify-between items-center">
          <h2 class="text-xl font-semibold text-gray-800">Team Members</h2>
          <v-btn 
            color="primary" 
            rounded 
            class="px-4" 
            @click="dialog = true"
          >
            <v-icon left>mdi-account-plus</v-icon>
            Add Member
          </v-btn>
        </div>
        
        <div class="card-body">
          <div class="bg-white p-5 rounded-lg shadow-sm">
            <div class="info-alert flex items-center p-4 mb-6 bg-blue-50 rounded-lg border-l-4 border-blue-400">
              <v-icon color="info" class="mr-3">mdi-information-outline</v-icon>
              <span class="text-sm text-blue-800">
                Team members must have updated their email addresses in their profiles before they can be added.
              </span>
            </div>
            
            <v-data-table 
              :loading="isUsersLoading" 
              :items="usersList" 
              :headers="headers"
              class="elevation-0 team-table"
              :footer-props="{
                'items-per-page-options': [5, 10, 15],
                'items-per-page-text': 'Members per page'
              }"
            >
              <template v-slot:item.profilePic="{ item }">
                <div class="user-avatar">
                  <v-avatar size="40" color="primary" v-if="!item.profilePic">
                    <span class="white--text text-h6">{{ getInitials(item.display_name) }}</span>
                  </v-avatar>
                  <v-avatar size="40" v-else>
                    <img :src="item.profilePic" :alt="item.display_name">
                  </v-avatar>
                </div>
              </template>
              
              <template v-slot:item.display_name="{ item }">
                <div class="font-medium text-gray-800">{{ item.display_name || "Guest User" }}</div>
              </template>
              
              <template v-slot:item.email="{ item }">
                <div class="text-gray-600">{{ item.email }}</div>
              </template>
              
              <template v-slot:item.status="{ item }">
                <v-chip
                  small
                  :color="item.status === 'accepted' ? 'success' : 'warning'"
                  text-color="white"
                  class="font-medium"
                >
                  {{ item.status }}
                </v-chip>
              </template>
              
              <template v-slot:item.action="{ item }">
                <div class="action-buttons">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        @click="editUser(item)"
                        class="mr-2"
                      >
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit Member</span>
                  </v-tooltip>
                  
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                        @click="removeUser(item)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Remove Member</span>
                  </v-tooltip>
                </div>
              </template>
              
              <template v-slot:no-data>
                <div class="text-center py-6">
                  <v-icon size="64" color="grey lighten-2">mdi-account-group-outline</v-icon>
                  <p class="mt-2 text-gray-500">No team members added yet</p>
                  <v-btn color="primary" class="mt-4" @click="dialog = true">Add Your First Team Member</v-btn>
                </div>
              </template>
            </v-data-table>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Add/Edit Member Dialog -->
    <v-dialog v-model="dialog" persistent max-width="500">
      <v-card class="rounded-lg">
        <v-card-title class="headline px-6 py-4 bg-primary text-white">
          {{ dialogTitle }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pa-6">
          <p class="mb-4 text-gray-600">Enter the details of the team member you want to add to your workspace.</p>
          <v-text-field
            v-model="name"
            label="Full Name"
            placeholder="John Doe"
            outlined
            :rules="[v => !!v || 'Name is required']"
            hide-details="auto"
            prepend-inner-icon="mdi-account-outline"
            class="mb-4"
          ></v-text-field>
          <v-text-field
            v-model="email"
            label="Email Address"
            placeholder="colleague@example.com"
            outlined
            :rules="[v => !!v || 'Email is required', v => isValidEmail(v) || 'Enter a valid email']"
            hide-details="auto"
            prepend-inner-icon="mdi-email-outline"
            class="mb-2"
          ></v-text-field>
        </v-card-text>
        
        <v-card-actions class="px-6 pb-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false" :disabled="loading">Cancel</v-btn>
          <v-btn 
            color="primary" 
            @click="saveUser" 
            :loading="loading"
            :disabled="!email || !isValidEmail(email)"
          >
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DialogCard from "@/components/ui/DialogCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import { auth, colUsers, db } from "@/utils/firebase.utils";
import _ from "lodash";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/utils/firebase.utils";
import {
  arrayRemove,
  arrayUnion,
  doc,
  onSnapshot,
  documentId,
  getDoc,
  getDocs,
  limit,
  query,
  updateDoc,
  where,
  deleteDoc,
  collection,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

export default {
  components: { 
    DialogCard,
    PageHeader
  },
  data() {
    return {
      dialog: false,
      workspaceDialog: false,
      loading: false,
      email: "",
      name: "",
      userId: null,
      unsubscribers: [],
      isEditMode: false,
      usersList: [],
      isUsersLoading: false,
      workspaces: [],
      headers: [
        {
          text: "",
          value: "profilePic",
          width: "60px",
          sortable: false,
          class: "text-uppercase primary white--text"
        },
        {
          text: "Name",
          value: "display_name",
          class: "text-uppercase primary white--text",
        },
        {
          text: "Email",
          value: "email",
          class: "text-uppercase primary white--text",
        },
        {
          text: "Status",
          value: "status",
          class: "text-uppercase primary white--text",
          width: "120px"
        },
        {
          text: "Actions",
          value: "action",
          align: "center",
          sortable: false,
          width: "120px",
          class: "text-uppercase primary white--text",
        },
      ],
    };
  },
  mounted() {
    this.fetchUsers();
  },
  computed: {
    dialogTitle() {
      return this.isEditMode ? "Edit Team Member" : "Add Team Member";
    },
  },
  beforeDestroy() {
    this.unsubscribers.forEach((unsubscribe) => unsubscribe());
    this.unsubscribers = [];
  },
  methods: {
    getInitials(name) {
      if (!name) return 'G';
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    },
    async removeUser({ id }) {
      const vm = this;
      vm.$confirm.show({
        message: "Are you sure you want to remove this team member?",
        onConfirm: async () => {
          try {
            vm.$loader.show();
            const removeUserFunction = httpsCallable(functions, 'removeUserFunction');
            const response = await removeUserFunction({ sender_id: vm.uid, uid: id });
            if (response.data.success) {
              vm.usersList = vm.usersList.filter(user => user.id !== id);
              vm.fetchUsers();
              vm.$alert.show("Team member removed successfully");
            } else {
              vm.$alert.show("Failed to remove team member. Please try again.");
            }
            vm.$loader.hide();
          } catch (error) {
            vm.handleError(error);
          }
        },
      });
    },
    async editUser(user) {
      this.$store.dispatch('addTeamMember', true);
      this.$router.push({ name: 'Assign-workspace', params: { email: user.email, uid: user.id, edit: true } });
    },
    async addingNewUser() {
      const vm = this;
      try {
        const mail = vm.email;
        const userName = vm.name;
        vm.email = "";
        vm.name = "";
        vm.userId = null;
        vm.isEditMode = false;
        vm.dialog = false;
        vm.loading = false;
        vm.fetchUsers();
        vm.$store.dispatch('addTeamMember', true);
        this.$router.push({ 
          name: 'Assign-workspace', 
          params: { 
            email: mail, 
            uid: mail,
            name: userName 
          } 
        });
      } catch (error) {
        vm.handleError(error);
      }
    },
    isValidEmail(email) {
      if (!email) return false;
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailPattern.test(email);
    },
    async saveUser() {
      const vm = this;
      try {
        if (!vm.name) {
          vm.$alert.show("Please enter the team member's name");
          return;
        }
        if (!this.isValidEmail(vm.email)) {
          vm.$alert.show("Please enter a valid email address");
          return;
        }
        vm.loading = true;
        let userQuery = query(
          colUsers,
          where("email", "==", vm.email),
          limit(1)
        );
        let userDocs = (await getDocs(userQuery)).docs;
        let user = (await getDoc(doc(colUsers, vm.uid))).data();
        let users = user.users || [];
        if (userDocs[0] == null) {
          if (users.indexOf(vm.email) != -1) {
            vm.loading = false;
            vm.$alert.show("User already exists.");
            return;
          }
          this.addingNewUser();
          return;
        }
        let uid = userDocs[0].id;
        if (this.email == user.email) {
          vm.loading = false;
          vm.$alert.show("Cannot add your own account");
          return;
        }
        if (vm.isEditMode) {
          // For edit mode
          // Update logic here if needed
        } else {
          // For add mode
          if (users.indexOf(uid) != -1) {
            vm.loading = false;
            vm.$alert.show("User already exists.");
            return;
          }
        }
        const userName = vm.name;
        vm.email = "";
        vm.name = "";
        vm.userId = null;
        vm.isEditMode = false;
        vm.dialog = false;
        vm.loading = false;
        vm.fetchUsers();
        // Set the Vuex state to indicate a team member has been added
        this.$store.dispatch('addTeamMember', true);
        // Navigate to the new page
        this.$router.push({ 
          name: 'Assign-workspace', 
          params: { 
            email: vm.email, 
            uid: uid,
            name: userName 
          } 
        });
      } catch (error) {
        vm.handleError(error);
      }
    },
    async fetchUsers() {
      const vm = this;
      try {
        vm.isUsersLoading = true;

        // Listen to changes in the current user document in real-time
        const currentUserRef = doc(colUsers, vm.uid);

        const unsubscribe = onSnapshot(currentUserRef, async (currentUserDoc) => {
          let userIds = currentUserDoc.data().users || [];

          if (userIds.length === 0) {
            vm.usersList = [];
            vm.isUsersLoading = false;
            return;
          }

          let users = [];

          // Separate valid document IDs from emails
          const docIds = [];
          const emailIds = [];

          for (let id of userIds) {
            // If the ID contains "@" assume it's an email, else it's a valid Firebase document ID
            if (id.includes("@")) {
              emailIds.push(id);
            } else {
              docIds.push(id);
            }
          }

          // Directly add email users to the users list with default data

          // Now handle Firebase document IDs in chunks
          for (let ids of _.chunk(docIds, 10)) {
            let userQuery = query(colUsers, where(documentId(), "in", ids));

            const unsubscribe1 = onSnapshot(userQuery, async (snapshot) => {
              for (const userDoc of snapshot.docs) {
                const userData = userDoc.data();
                const userId = userDoc.id;

                // Listen to the associated team document for this user
                const teamDocRef = doc(db, "users", vm.uid, "team", userId);

                const unsubscribe2 = onSnapshot(teamDocRef, (teamDoc) => {
                  let status = 'pending';
                  let workspaceData;
                  if (teamDoc.exists()) {
                    const teamData = teamDoc.data();
                    status = teamData.workSpaceToBeAdded?.status ? "accepted" : "pending";
                    workspaceData = teamData.workSpaceToBeAdded.workspaceData;
                  }

                  // Update or add the user in the users list
                  const userIndex = users.findIndex((u) => u.id === userId);
                  if (userIndex > -1) {
                    users[userIndex] = { ...userData, id: userId, status, workspaceData };
                  } else {
                    users.push({ ...userData, id: userId, status, workspaceData });
                  }

                  // Update the usersList with real-time data
                  vm.usersList = [...users];
                  emailIds.forEach(email => {
                    vm.usersList.push({
                      id: email,
                      display_name: "Guest",
                      email: email,
                      status: "pending"
                    });
                  });
                });
                this.unsubscribers.push(unsubscribe2);
              }
            });
            this.unsubscribers.push(unsubscribe1);
          }
        });
        this.unsubscribers.push(unsubscribe);

      } catch (error) {
        vm.handleError(error);
      } finally {
        vm.isUsersLoading = false;
      }
    }
  },
};
</script>

<style scoped>
.teams-page {
  min-height: 100vh;
}

.container {
  max-width: 1280px;
}

.card {
  background-color: transparent;
  border-radius: 0.75rem;
  overflow: hidden;
}

.card-header {
  padding: 1.25rem 1.5rem;
  background-color: transparent;
}

.card-body {
  padding: 0;
}

.info-alert {
  transition: all 0.3s ease;
}

.team-table ::v-deep .v-data-table__wrapper {
  border-radius: 8px;
  overflow: hidden;
}

.team-table ::v-deep .v-data-table-header th {
  font-weight: 600 !important;
  font-size: 0.8rem !important;
  letter-spacing: 0.05em !important;
}

.team-table ::v-deep .v-data-table-header {
  background-color: #f8fafc !important;
}

.team-table ::v-deep .v-data-table__wrapper table tbody tr:hover {
  background-color: rgba(25, 118, 210, 0.04) !important;
}

.action-buttons {
  display: flex;
  justify-content: center;
}

.user-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .container {
    padding-left: 1rem;
    padding-right: 1rem;
  }
  
  .card-header {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
  }
  
  .card-header .v-btn {
    margin-top: 0.5rem;
  }
}
</style>
